import { showConfirm } from '../components/ConfirmDialog';
/**
 * Stage Plot Editor Page
 *
 * Full-page editor view. Manages the list of stage plots for a rider,
 * creates new ones, and loads/saves via the backend API.
 *
 * Route: /band/:bandId/stageplot-editor/:riderId?
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useParams, useSearchParams } from 'react-router-dom';
import { useProfileContext } from '../hooks/useProfileContext';
import StagePlotEditor from '../components/StagePlotEditor';
import StagePlotAssistant from '../components/StagePlotEditor/StagePlotAssistant';
import StagePlotThumb from '../components/StagePlotEditor/StagePlotThumb';
import { normalizePlot } from '../components/StagePlotEditor/StagePlotCanvas';
import ErrorBoundary from '../components/StagePlotEditor/ErrorBoundary';
import {
  listStagePlots,
  createStagePlot,
  updateStagePlot,
  deleteStagePlot,
  StagePlotApiError,
  type StagePlotListItem,
} from '../api/stageplots';
import type { StagePlot } from '../shared/types/stageplot';
import { createEmptyStagePlot } from '../shared/types/stageplot';
import { useWriteGate } from '../lib/writeGate';
import { useMusicians } from '../hooks/useMusicians';
import { useCrew } from '../hooks/useCrew';
import { useConfig } from '../hooks/useConfig';
import { generateStagePlotDraft, type StageReport } from '../lib/stageplotGenerate';
import { apiAiMapStageItems, apiAiCredits, type StageItemMapping, type StageCommandAction } from '../api';
import { PALETTE_ITEMS, CATEGORY_COLORS, type StageElement } from '../shared/types/stageplot';
import type { StagePlotEditorHandle } from '../components/StagePlotEditor';
import './StagePlotEditorPage.css';
import { withPageIcon } from '../lib/uiIcons';

export default function StagePlotEditorPage() {
  const { t } = useTranslation();
  const { riderId } = useParams<{ riderId?: string }>();
  const { activeProfileId } = useProfileContext();
  const [searchParams] = useSearchParams();

  const [plots, setPlots] = useState<StagePlotListItem[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [plotName, setPlotName] = useState('');
  const [currentData, setCurrentData] = useState<StagePlot | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [exportCb] = useState<(() => void) | null>(null);

  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Band data for the deterministic draft generator (read-only here — the
  // hooks' autosave only fires on their own setters, which we never call).
  const { musicians } = useMusicians(activeProfileId ?? undefined);
  const { crew } = useCrew(activeProfileId ?? undefined);
  const { config } = useConfig();
  const editorRef = useRef<StagePlotEditorHandle | null>(null);

  // "Generate draft" panel state.
  const [genOpen, setGenOpen] = useState(false);
  const [genWidth, setGenWidth] = useState(12);
  const [genDepth, setGenDepth] = useState(8);
  const [genUnit, setGenUnit] = useState<'m' | 'ft'>('m');
  const [genIncludeCrew, setGenIncludeCrew] = useState(true);
  const [genReport, setGenReport] = useState<StageReport | null>(null);
    const [genError, setGenError] = useState<string | null>(null);

    // "Ask AI to map the rest" panel state (LOT B). The AI proposes instrument/role
    // -> palette-icon mappings; the user confirms each (checkbox) and the draft
    // is completed IN the editor. Always read-only server-side.
    const [aiMapOpen, setAiMapOpen] = useState(false);
    const [aiMapping, setAiMapping] = useState(false);
    const [aiMappings, setAiMappings] = useState<StageItemMapping[] | null>(null);
    const [aiChecked, setAiChecked] = useState<Record<string, boolean>>({});
    const [aiError, setAiError] = useState<string | null>(null);
    const [aiCredits, setAiCredits] = useState<number | null>(null);
    const [aiUnlimited, setAiUnlimited] = useState(false);
    const [aiTruncated, setAiTruncated] = useState(false);

    // Load the AI credit balance once (badge + gating).
    useEffect(() => {
      apiAiCredits().then((d) => { setAiCredits(d.credits); setAiUnlimited(!!d.unlimited); }).catch(() => setAiCredits(null));
    }, []);
    const aiOutOfCredits = !aiUnlimited && aiCredits !== null && aiCredits <= 0;

    // Open the AI mapping panel for the current unmapped report items.
    const openAiMap = useCallback(() => {
      if (!genReport || genReport.unmapped.length === 0) return;
      setAiError(null);
      setAiMappings(null);
      setAiChecked({});
      setAiTruncated(false);
      setAiMapOpen(true);
    }, [genReport]);

    // Call the AI to propose mappings for the unmapped items.
    const handleAiMap = useCallback(async () => {
      if (!activeProfileId || !genReport || aiMapping) return;
      setAiMapping(true);
      setAiError(null);
      try {
        const items = genReport.unmapped.map((u) => ({
          name: u.name,
          kind: 'instrument' as const,
        }));
        const result = await apiAiMapStageItems({ bandId: activeProfileId, items });
        setAiMappings(result.mappings);
        setAiTruncated(!!result.truncated);
        setAiCredits(result.remaining);
        setAiUnlimited(!!result.unlimited);
        // Default: every mapping checked EXCEPT confidence < 0.5.
        const checked: Record<string, boolean> = {};
        for (const m of result.mappings) {
          checked[m.name] = (m.confidence ?? 0) >= 0.5;
        }
        setAiChecked(checked);
      } catch (err) {
        setAiError(err instanceof Error ? err.message : 'AI mapping failed');
      } finally {
        setAiMapping(false);
      }
    }, [activeProfileId, genReport, aiMapping]);

    // Build a StageElement from a confirmed mapping (palette defaults + category
    // colour), placed at a free-ish spot near the front of the stage.
    const buildMappedElement = useCallback((m: StageItemMapping): StageElement | null => {
      if (!m.iconId) return null;
      const item = PALETTE_ITEMS.find((i) => i.iconId === m.iconId);
      if (!item) return null;
      const category = item.category;
      return {
        id: `ai-${m.iconId}-${Date.now().toString(36)}-${Math.random().toString(36).substr(2, 5)}`,
        iconId: item.iconId,
        category,
        label: m.name || item.label,
        x: 0.5,
        y: 0.5,
        width: item.defaultWidth,
        height: item.defaultHeight,
        rotation: 0,
        color: CATEGORY_COLORS[category],
      };
    }, []);

    // Confirm the checked mappings: append the elements to the editor draft.
    const handleAiConfirm = useCallback(() => {
      if (!aiMappings) return;
      const elements: StageElement[] = [];
      for (const m of aiMappings) {
        if (aiChecked[m.name]) {
          const el = buildMappedElement(m);
          if (el) elements.push(el);
        }
      }
      if (elements.length === 0) return;
      editorRef.current?.addElements(elements);
      // Keep the page's currentData in sync with the editor (onChange fires too,
      // but the editor's internal state is the source of truth for the canvas).
      setAiMapOpen(false);
      setAiMappings(null);
      setGenReport((prev) => (prev ? { ...prev, unmapped: [] } : prev));
    }, [aiMappings, aiChecked, buildMappedElement]);

  // Capability-aware WRITE gate (lot « delegated content », 2026-09-25). EVERY
  // write on this page goes to `/api/stageplots`, now exempted from the global
  // read-only guard for `content.rider` (conditional pre-guard mount in
  // server/index.js, band resolved from the ENTITY). A globally `read-only`
  // account WITHOUT the capability would get a 403 on save and read a bogus red
  // banner: the cut is made HERE, at the single source of every write handler,
  // BEFORE any network call. The canvas is never frozen per-control (that would
  // break local drag/undo state) — the write is simply suppressed, and the
  // app-wide banner already explains a read-only account. Never the blanket
  // `canWrite`: that would freeze the very crew member the capability exists for.
  const writeGate = useWriteGate();
  const canWriteContent = writeGate.canWriteCap('content.rider');

  // A suppressed write must NEVER be silent: an account without `content.rider`
  // that clicks Save / Rename / Delete / Import would otherwise see nothing
  // happen and read the OLD value back (bug user 2026-10-07: renaming a plot
  // from its card appeared to be ignored). The note explains WHY, and every
  // write control is disabled so the doomed attempt isn't even offered.
  const readOnlyNote = canWriteContent ? null : (
    <div className="stageplot-readonly-note" role="status">{t('stagePlotEditor.readOnlyNotice')}</div>
  );

  const loadPlots = useCallback(async () => {
    // Never list with an empty band id: the API would answer for "no band"
    // (cross-profile noise) and the list would look wrong during the profile
    // switch. Stay idle until the active band is known (audit fix F3).
    if (!activeProfileId) {
      setPlots([]);
      setListError(null);
      setLoadingList(false);
      return;
    }
    setLoadingList(true);
    setListError(null);
    try {
      const result = await listStagePlots({
        riderId: riderId ?? undefined,
        bandId: activeProfileId,
        limit: 50,
      });
      setPlots(result.items);
    } catch (err) {
      setListError(err instanceof StagePlotApiError ? err.message : 'Failed to load stage plots');
    } finally {
      setLoadingList(false);
    }
  }, [activeProfileId, riderId]);

  useEffect(() => { void loadPlots(); }, [loadPlots]);

  const handleEdit = useCallback((plot: StagePlotListItem) => {
    setEditingId(plot.id);
    setPlotName(plot.name);
    setCurrentData(plot.data);
    setSaveStatus('idle');
    setSaveMessage(null);
  }, []);

  // Rename a stage plot / imported image inline from the list (no need to open
  // the editor). Persists name only, preserving kind/imageUrl/elements.
  const [renameId, setRenameId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const startRename = (plot: StagePlotListItem) => {
    setRenameId(plot.id);
    setRenameValue(plot.name);
  };
  const commitRename = async (id: string) => {
    const name = renameValue.trim();
    setRenameId(null);
    if (!name || !activeProfileId) return;
    // content.rider cut: suppress the doomed write, keep local state intact.
    if (!canWriteContent) return;
    try {
      await updateStagePlot(id, { name, bandId: activeProfileId });
      await loadPlots();
    } catch (e) {
      setListError(e instanceof StagePlotApiError ? e.message : 'Rename failed');
    }
  };


  // Deep-link: ?edit=<id> (e.g. from Rider "Create new") opens the editor
  // directly on the matching plot once the list has loaded.
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (!editId || plots.length === 0 || editingId) return;
    const plot = plots.find((p) => p.id === editId);
    if (plot) handleEdit(plot);
  }, [plots, searchParams, editingId, handleEdit]);

  const handleCreate = useCallback(() => {
    setEditingId(null);
    setPlotName(`Stage Plot ${plots.length + 1}`);
    setCurrentData(createEmptyStagePlot());
    setSaveStatus('idle');
    setSaveMessage(null);
  }, [plots.length]);

  // Hidden file input used by the "Import image" button. Imported stage plots
  // are stored as kind:'image' with a PNG/JPEG data URL (many bands already
  // have their stage plot as a picture). (2026-08-11)
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);

  const handleImportImage = useCallback(async (file: File) => {
    // content.rider cut: no capability ⇒ no write (and no file reading work).
    if (!canWriteContent) return;
    setImporting(true);
    setImportError(null);
    try {
      let dataUrl: string;
      if (file.type === 'image/png' || file.type === 'image/jpeg') {
        dataUrl = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result));
          reader.onerror = () => reject(new Error('Could not read file'));
          reader.readAsDataURL(file);
        });
      } else {
        // Re-encode (WebP/GIF/AVIF/other) to PNG via canvas so pdfmake can
        // embed it and the preview is consistent.
        dataUrl = await new Promise<string>((resolve, reject) => {
          const img = new Image();
          const url = URL.createObjectURL(file);
          img.onload = () => {
            try {
              const canvas = document.createElement('canvas');
              canvas.width = img.width || 800;
              canvas.height = img.height || 600;
              const ctx = canvas.getContext('2d');
              if (!ctx) { URL.revokeObjectURL(url); reject(new Error('Canvas unavailable')); return; }
              ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
              URL.revokeObjectURL(url);
              resolve(canvas.toDataURL('image/png'));
            } catch (e) { URL.revokeObjectURL(url); reject(e); }
          };
          img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Unsupported image')); };
          img.src = url;
        });
        if (!dataUrl) throw new Error('Image conversion failed');
      }
      if (!activeProfileId) throw new Error('Select a band first');
      const baseName = file.name.replace(/\.[^.]+$/, '') || 'Stage Plot Image';
      const created = await createStagePlot({
        bandId: activeProfileId,
        name: baseName,
        data: {
          ...createEmptyStagePlot(baseName),
          kind: 'image',
          imageUrl: dataUrl,
          metadata: { ...createEmptyStagePlot(baseName).metadata, bandName: '', venue: '', date: '', notes: '' },
        },
      });
      await loadPlots();
      setEditingId(created.id);
      setPlotName(created.name);
    } catch (e) {
      setImportError(e instanceof Error ? e.message : 'Import failed');
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [activeProfileId, loadPlots, canWriteContent]);


  const handleExportImage = useCallback(() => {
    if (exportCb) exportCb();
  }, [exportCb]);

  const handleSave = useCallback(async () => {
    if (!currentData) return;
    // content.rider cut: a globally read-only account WITHOUT the capability
    // must not fire the write (server would 403 → bogus red banner).
    if (!canWriteContent) return;
    if (!activeProfileId) return;
    setSaving(true);
    setSaveStatus('saving');
    setSaveMessage(null);
    const plotData: StagePlot = {
      ...currentData,
      name: plotName || currentData.name,
      metadata: { ...currentData.metadata, lastModified: Date.now() },
    };
    try {
      if (editingId) {
        await updateStagePlot(editingId, {
          name: plotName,
          bandId: activeProfileId,
          data: plotData,
        });
      } else {
        await createStagePlot({
          name: plotName,
          riderId: riderId || undefined,
          bandId: activeProfileId,
          data: plotData,
        });
      }
      setSaveStatus('saved');
      setSaveMessage('Stage plot saved successfully');
      await loadPlots();
    } catch (err) {
      setSaveStatus('error');
      setSaveMessage(err instanceof StagePlotApiError ? err.message : 'Failed to save stage plot');
    } finally {
      setSaving(false);
    }
  }, [activeProfileId, currentData, editingId, plotName, riderId, loadPlots, canWriteContent]);

  const handleDataChange = useCallback((updated: StagePlot) => {
      setCurrentData(updated);
      setSaveStatus('idle');
      setSaveMessage(null);
    }, []);

    // Apply validated assistant actions to the editor store (undoable). The
    // editor's onChange fires on every store change, so currentData stays in
    // sync and the normal Save persists the plot. Nothing is written server-side.
    const handleAssistantApply = useCallback((actions: StageCommandAction[]) => {
      editorRef.current?.applyActions(actions);
    }, []);

  // Open the "Generate draft" panel, pre-filling stage size from the band's
  // rider config (stage.van.width/depth) when set, else the 12×8 m default.
  const openGenerate = useCallback(() => {
    const van = config?.stage?.van;
    const w = van && van.width > 0 ? van.width : 12;
    const d = van && van.depth > 0 ? van.depth : 8;
    setGenWidth(w);
    setGenDepth(d);
    setGenUnit('m');
    setGenIncludeCrew(true);
    setGenReport(null);
    setGenError(null);
    setGenOpen(true);
  }, [config]);

  const handleGenerate = useCallback(async () => {
    if (!currentData) return;
    // content.rider cut: no capability ⇒ no draft (it would be unsavable).
    if (!canWriteContent) return;
    setGenError(null);
    setGenReport(null);

    const risers = (config?.stageDesign?.van?.risers ?? []).map((r) => ({
      name: r.name || 'Riser',
      // Pass the raw text dimension (e.g. "1m", "0,60m", "0.40m") to the
      // generator: it parses meters (comma OR dot decimal) robustly and falls
      // back to a canonical default when unparseable.
      width: r.width ?? 2,
      depth: r.depth ?? 1.5,
      height: r.height ?? 0.4,
      position: r.position || '',
      quantity: r.quantity || 1,
    }));

    const backlineItems = (config?.backline?.van?.items ?? []).map((b) => ({
      label: b.label || b.type || '',
      type: b.type || '',
      quantity: b.quantity || 1,
    }));

    const { plot, report } = generateStagePlotDraft({
      bandName: activeProfileId || '',
      stageDimensions: { width: genWidth, height: genDepth, unit: genUnit },
      musicians: musicians.map((m) => ({
        id: m.id,
        name: m.name,
        instruments: m.instruments.map((i) => ({ name: i.name })),
      })),
      crew: genIncludeCrew
        ? crew.map((c) => ({ name: c.name, role: c.role }))
        : [],
      backlineItems,
      risers,
      plotName: plotName || undefined,
    });

    // If the editor already holds elements, ask before replacing them.
    if (currentData.elements.length > 0) {
      const ok = await showConfirm(
        t('stagePlotEditor.replaceDraftConfirm', {
          defaultValue:
            'Generate a draft will replace the current elements on the canvas. Continue?',
        }),
      );
      if (!ok) return;
    }

    editorRef.current?.loadDraft(plot);
    setCurrentData(plot);
    setGenReport(report);
    setGenOpen(false);
  }, [currentData, canWriteContent, config, genWidth, genDepth, genUnit, genIncludeCrew, musicians, crew, plotName, t]);

  const handleDelete = useCallback(async (id: string) => {
    // content.rider cut: suppress the doomed delete for a non-holder.
    if (!canWriteContent) return;
    setDeletingId(id);
    try {
      if (!activeProfileId) return;
      await deleteStagePlot(id, activeProfileId);
      setPlots((prev) => prev.filter((p) => p.id !== id));
      if (editingId === id) {
        setEditingId(null);
        setCurrentData(null);
        setPlotName('');
        setSaveStatus('idle');
        setSaveMessage(null);
      }
    } catch (err) {
      setSaveStatus('error');
      setSaveMessage(err instanceof StagePlotApiError ? err.message : 'Failed to delete stage plot');
    } finally {
      setDeletingId(null);
    }
  }, [activeProfileId, editingId, canWriteContent]);

  const handleBackToList = useCallback(() => {
    setEditingId(null);
    setCurrentData(null);
    setPlotName('');
    setSaveStatus('idle');
    setSaveMessage(null);
  }, []);
  // Use the loaded band's stable UUID in band-scoped links. A name/slug can be
  // temporarily unavailable while ProfileProvider refreshes after login, which
  // otherwise makes the Back link point at a route App.tsx cannot match.
  const base = activeProfileId ? `/band/${activeProfileId}` : '';

  if (currentData) {
    return (
      <ErrorBoundary label="Stage Plot Editor">
      <div className="stageplot-editor-page" data-stageplot-view="editor">
        {readOnlyNote}
        <div className="editor-header">
          <div className="editor-header-left">
            <button className="btn btn-ghost btn-small" onClick={handleBackToList} aria-label={t('stagePlotEditor.backToListAria')}>← {t('stagePlotEditor.back')}</button>
            <input
              type="text"
              className="editor-title-input"
              value={plotName}
              onChange={(e) => setPlotName(e.target.value)}
              placeholder={t('stagePlotEditor.namePh')}
              aria-label={t('stagePlotEditor.namePh')}
            />
          </div>
          <div className="editor-header-right">
            <span className={`editor-save-status ${saveStatus === 'saved' ? 'success' : saveStatus === 'error' ? 'error' : ''}`}>
              {saveStatus === 'saving' && t('stagePlotEditor.saving')}
              {saveStatus === 'saved' && t('stagePlotEditor.saved')}
              {saveStatus === 'error' && saveMessage}
              {saveStatus === 'idle' && ' '}
            </span>
            {editingId && (
              <button
                className="btn btn-danger btn-small"
                onClick={async () => { if (await showConfirm(t('stagePlotEditor.deleteConfirm', { name: plotName }))) void handleDelete(editingId); }}
                disabled={deletingId === editingId || !canWriteContent}
              >
                {deletingId === editingId ? t('stagePlotEditor.deleting') : t('stagePlotEditor.delete')}
              </button>
            )}
            <button className="btn btn-secondary btn-small" onClick={handleExportImage} aria-label={t('stagePlotEditor.exportAria')}>{t('stagePlotEditor.exportPng')}</button>
                        <button className="btn btn-secondary btn-small" onClick={openGenerate} disabled={!canWriteContent} title={t('stagePlotEditor.generateDraftTitle')}>
                          {t('stagePlotEditor.generateDraft')}
                        </button>
                        <button className="btn btn-primary" onClick={() => void handleSave()} disabled={saving || !canWriteContent}>
                          {saving ? t('stagePlotEditor.saving') : t('common.save')}
                        </button>
                      </div>
                    </div>
                    <div className="stageplot-credit">🙏 {t('stagePlotEditor.stagePlotProCredit')}</div>
                    {genOpen && (
                      <div className="stageplot-generate-panel" data-stageplot-generate>
                        <div className="stageplot-generate-title">{t('stagePlotEditor.generateDraft')}</div>
                        <div className="stageplot-generate-row">
                          <label>
                            {t('stagePlotEditor.stageWidth')}
                            <input type="number" min={1} value={genWidth} onChange={(e) => setGenWidth(Number(e.target.value) || 0)} />
                          </label>
                          <label>
                            {t('stagePlotEditor.stageDepth')}
                            <input type="number" min={1} value={genDepth} onChange={(e) => setGenDepth(Number(e.target.value) || 0)} />
                          </label>
                          <label>
                            {t('stagePlotEditor.unit')}
                            <select value={genUnit} onChange={(e) => setGenUnit(e.target.value as 'm' | 'ft')}>
                              <option value="m">m</option>
                              <option value="ft">ft</option>
                            </select>
                          </label>
                        </div>
                        <label className="stageplot-generate-check">
                          <input type="checkbox" checked={genIncludeCrew} onChange={(e) => setGenIncludeCrew(e.target.checked)} />
                          {t('stagePlotEditor.includeCrew')}
                        </label>
                        {genError && <div className="stageplot-error"><p>⚠️ {genError}</p></div>}
                        <div className="stageplot-generate-actions">
                          <button className="btn btn-primary" onClick={handleGenerate}>{t('stagePlotEditor.generate')}</button>
                          <button className="btn btn-ghost btn-small" onClick={() => setGenOpen(false)}>{t('common.cancel')}</button>
                        </div>
                      </div>
                    )}
                    {genReport && (
                      <div className="stageplot-generate-report" data-stageplot-report>
                        <div className="stageplot-generate-title">{t('stagePlotEditor.draftReport')}</div>
                        <p>{t('stagePlotEditor.placedCount', { count: genReport.placedCount })}</p>
                        {genReport.stageTooSmall && <p>⚠️ {t('stagePlotEditor.stageTooSmall')}</p>}
                        {genReport.warnings.length > 0 && (
                          <ul>{genReport.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
                        )}
                        {genReport.unmapped.length > 0 && (
                                                  <div className="stageplot-generate-unmapped">
                                                    <p>{t('stagePlotEditor.unmappedTitle')}</p>
                                                    <ul>
                                                      {genReport.unmapped.map((u, i) => (
                                                        <li key={i}>{u.name} — {u.reason}</li>
                                                      ))}
                                                    </ul>
                                                    <div className="stageplot-generate-unmapped-actions">
                                                      <button className="btn btn-secondary btn-small" onClick={() => setGenOpen(true)}>
                                                        {t('stagePlotEditor.addManually')}
                                                      </button>
                                                      <button
                                                        className="btn btn-primary btn-small"
                                                        onClick={openAiMap}
                                                        disabled={aiOutOfCredits}
                                                        title={t('stagePlotEditor.askAiMapTitle')}
                                                      >
                                                        {t('stagePlotEditor.askAiMap')}
                                                      </button>
                                                    </div>
                                                    {aiOutOfCredits && (
                                                      <p className="stageplot-ai-credits-out">{t('stagePlotEditor.outOfCredits')}</p>
                                                    )}
                                                  </div>
                                                )}
                                                <button className="btn btn-ghost btn-small" onClick={() => setGenReport(null)}>{t('common.close')}</button>
                      </div>
                    )}
{aiMapOpen && (
                      <div className="stageplot-ai-map-panel" data-stageplot-ai-map>
                        <div className="stageplot-generate-title">
                          {t('stagePlotEditor.askAiMap')}
                          <span className="stageplot-ai-credits-badge">
                            {aiUnlimited ? '∞' : `${aiCredits ?? '…'} ${t('stagePlotEditor.credits')}`}
                          </span>
                        </div>
                        {aiError && <div className="stageplot-error"><p>⚠️ {aiError}</p></div>}
                        {aiTruncated && <p className="stageplot-ai-hint">{t('stagePlotEditor.aiTruncated')}</p>}
                        {!aiMappings ? (
                          <div className="stageplot-ai-map-actions">
                            <button
                              className="btn btn-primary"
                              onClick={() => void handleAiMap()}
                              disabled={aiMapping || aiOutOfCredits}
                            >
                              {aiMapping ? t('stagePlotEditor.aiMapping') : t('stagePlotEditor.askAiMapRun')}
                            </button>
                            <button className="btn btn-ghost btn-small" onClick={() => setAiMapOpen(false)}>{t('common.cancel')}</button>
                          </div>
                        ) : (
                          <div className="stageplot-ai-map-results">
                            {aiMappings.length === 0 ? (
                              <p className="stageplot-ai-hint">{t('stagePlotEditor.aiNoMappings')}</p>
                            ) : (
                              <ul className="stageplot-ai-map-list">
                                {aiMappings.map((m) => (
                                  <li key={m.name} className="stageplot-ai-map-row">
                                    <label>
                                      <input
                                        type="checkbox"
                                        checked={!!aiChecked[m.name]}
                                        onChange={(e) => setAiChecked((prev) => ({ ...prev, [m.name]: e.target.checked }))}
                                      />
                                      <span className="stageplot-ai-map-name">{m.name}</span>
                                      <span className="stageplot-ai-map-icon">
                                        {m.iconId ? `→ ${m.iconId}` : '—'}
                                      </span>
                                      <span className="stageplot-ai-map-conf">
                                        {m.confidence != null ? `${Math.round(m.confidence * 100)}%` : ''}
                                      </span>
                                    </label>
                                  </li>
                                ))}
                              </ul>
                            )}
                            <div className="stageplot-ai-map-actions">
                              <button
                                className="btn btn-primary"
                                onClick={handleAiConfirm}
                                disabled={aiMappings.length === 0}
                              >
                                {t('stagePlotEditor.aiConfirm')}
                              </button>
                              <button className="btn btn-ghost btn-small" onClick={() => setAiMapOpen(false)}>{t('common.cancel')}</button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                                          <div className="editor-body">
          {currentData?.kind === 'image' && currentData.imageUrl ? (
            <div style={{ textAlign: 'center', padding: 16 }}>
              <img
                src={currentData.imageUrl}
                alt={plotName}
                style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain', border: '1px solid var(--border-light)', borderRadius: 8 }}
              />
              <p style={{ color: 'var(--text-dim)', marginTop: 10, fontSize: 'var(--font-sm)' }}>
                {t('stagePlotEditor.imageViewOnly')}
              </p>
            </div>
          ) : (
                      <>
                        <StagePlotEditor initialJSON={currentData} onChange={handleDataChange} ref={editorRef} />
                        <StagePlotAssistant
                          bandId={activeProfileId || ''}
                          elements={currentData.elements}
                          stageDimensions={currentData.stageDimensions}
                          onApply={handleAssistantApply}
                          canWrite={canWriteContent}
                        />
                      </>
                    )}
        </div>
      </div>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary label="Stage Plot List">
    <div className="page stageplot-list-page" data-stageplot-view="list">
      {readOnlyNote}
      {/* P3d: shared Roadcase header (mono/uppercase title). `.page-header-actions`
          rides ALONGSIDE `rc-header-actions`: it is the stable selector the
          StagePlotEditorPage vitest suite clicks for "New plot" and adds no
          styling of its own beyond index.css. */}
      <header className="rc-header">
        <div className="rc-header-text">
          <h1 className="rc-header-title">{withPageIcon('stagePlotEditor.title', t('stagePlotEditor.title'))}</h1>
          {riderId && <p className="rc-header-sub">{t('stagePlotEditor.linkedToRider')}</p>}
        </div>
        <div className="rc-header-actions page-header-actions">
          <NavLink to={`${base}/rider`} className="btn btn-ghost btn-small">← {t('stagePlotEditor.backToRider')}</NavLink>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif,image/avif"
            style={{ display: 'none' }}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) void handleImportImage(f); }}
          />
          <button className="btn btn-secondary btn-small" onClick={() => fileInputRef.current?.click()} disabled={importing || !canWriteContent}>
            {importing ? t('stagePlotEditor.importing') : t('stagePlotEditor.importImage')}
          </button>
          <button className="btn btn-primary" onClick={handleCreate}>{t('stagePlotEditor.newPlot')}</button>
        </div>
      </header>

      {importError && (
        <div className="stageplot-error">
          <p>⚠️ {importError}</p>
        </div>
      )}

      {loadingList && <div className="stageplot-loading">{t('stagePlotEditor.loading')}</div>}

      {listError && (
        <div className="stageplot-error">
          <p>⚠️ {listError}</p>
          <p className="stageplot-error-hint">{t('stagePlotEditor.importErrorHint')}</p>
          <div className="stageplot-error-actions">
            <button className="btn btn-secondary btn-small" onClick={() => void loadPlots()}>{t('stagePlotEditor.retry')}</button>
            <button className="btn btn-primary btn-small" onClick={handleCreate}>{t('stagePlotEditor.createAnyway')}</button>
          </div>
        </div>
      )}

      {!loadingList && !listError && plots.length === 0 && (
        <div className="stageplot-empty">
          <div className="stageplot-empty-icon">🎨</div>
          <h2>{t('stagePlotEditor.noPlots')}</h2>
          <p>{t('stagePlotEditor.emptyHint')}</p>
          <button className="btn btn-primary btn-large" onClick={handleCreate}>{t('stagePlotEditor.createFirst')}</button>
        </div>
      )}

      {!loadingList && !listError && plots.length > 0 && (
        <div className="stageplot-grid">
          {plots.map((plot) => (
            <div key={plot.id} className="stageplot-card card">
              <div className="card-header">
                {renameId === plot.id ? (
                  <input
                    autoFocus
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onBlur={() => void commitRename(plot.id)}
                    onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') setRenameId(null); }}
                    style={{ flex: 1, fontSize: 'var(--font-md)', minWidth: 0 }}
                  />
                ) : (
                  <h3 className="card-title">{plot.name}</h3>
                )}
                {plot.data.kind === 'image' && (
                  <span className="stageplot-kind-badge">🖼️ {t('stagePlotEditor.kindImage')}</span>
                )}
                {/* The card used to show `new Date(plot.updatedAt).toLocaleDateString()`
                    here. Removed on user request (2026-10-06): the date told the
                    reader nothing useful and competed with the plot name. Only the
                    RENDER is gone — `updatedAt` is still returned by the API and
                    still stored; nothing was written or migrated. */}
              </div>
              {plot.data.kind === 'image' && plot.data.imageUrl ? (
                <img
                  src={plot.data.imageUrl}
                  alt={plot.name}
                  style={{ width: '100%', maxHeight: 140, objectFit: 'contain', background: '#fff', borderRadius: 6, margin: '6px 0' }}
                />
              ) : (
                <>
                  <StagePlotThumb plot={plot.data} />
                  <div className="stageplot-card-info">
                    {(() => {
                      const np = normalizePlot(plot.data);
                      return (
                        <>
                          <span>{t('stagePlotEditor.elementCount', { count: np.elements.length })}</span>
                          <span>{np.stageDimensions.width}×{np.stageDimensions.height} {np.stageDimensions.unit}</span>
                        </>
                      );
                    })()}
                  </div>
                </>
              )}
              <div className="stageplot-card-actions">
                <button className="btn btn-primary btn-small" onClick={() => handleEdit(plot)}>{t('stagePlotEditor.open')}</button>
                <button className="btn btn-ghost btn-small" onClick={() => startRename(plot)} title={t('stagePlotEditor.renameAria')} disabled={!canWriteContent}>✏️</button>
                <button
                  className="btn btn-danger btn-small"
                  onClick={async () => { if (await showConfirm(t('stagePlotEditor.deleteConfirm', { name: plot.name }))) void handleDelete(plot.id); }}
                  disabled={deletingId === plot.id || !canWriteContent}
                >
                  {deletingId === plot.id ? '...' : t('stagePlotEditor.delete')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
    </ErrorBoundary>
  );
}